import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Fieldset, Input, Modal } from '@feathr/components';
import DonationFormsSelect from '@feathr/extender/components/DonationFormsSelect';
import { useExportModal } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';
import { flattenError } from '@feathr/hooks';

import DataFieldsSelect from './DataFieldsSelect';
import DateRangeSelect from './DateRangeSelect';

import * as styles from './ExportDonationsModal.css';

interface ISelectOption {
  id: string;
  name: string;
}

interface IProps {
  opened: boolean;
  onClose: () => void;
}

function ExportDonationsModal({ opened, onClose }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { DonationForms } = useStore();

  const {
    email,
    emailHadFocus,
    customDateRange,
    selectedDateRange,
    isLoading,
    isSubmitDisabled,
    validationErrors,
    dateRangeOptions,
    handleEmailBlur,
    handleChangeEmail,
    handleDateRangeChange,
    handleCustomDateRangeChange,
    handleExport,
    resetFormState,
    resolveDateRange,
  } = useExportModal(t);

  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [selectedOptionalFields, setSelectedOptionalFields] = useState<ISelectOption[]>([]);

  function handleSelectForms(values?: string[]): void {
    setSelectedForms(values ?? []);
  }

  function handleSelectOptionalFields(values: ISelectOption[]): void {
    setSelectedOptionalFields(values);
  }

  function handleClose(): void {
    resetFormState();
    setSelectedForms([]);
    setSelectedOptionalFields([]);
    onClose();
  }

  async function handleConfirm(): Promise<void> {
    const { startDate, endDate } = resolveDateRange();

    await handleExport(
      () =>
        DonationForms.exportDonations({
          email,
          start: startDate,
          end: endDate,
          form_ids: selectedForms.length > 0 ? selectedForms : undefined,
          fields: selectedOptionalFields.map((field) => field.id),
        }),
      handleClose,
      t,
    );
  }

  return (
    <Modal
      allowOverflow
      description={t(
        'Customize your export settings below. Your CSV file will be delivered via email with a secure download link since exporting may take a few minutes to process.',
      )}
      onClose={handleClose}
      opened={opened}
      rightActions={[
        <Button disabled={isLoading} key={'cancel'} onClick={handleClose}>
          {t('Cancel')}
        </Button>,
        <Button
          disabled={isSubmitDisabled}
          isLoading={isLoading}
          key={'confirm'}
          onClick={handleConfirm}
          type={'primary'}
        >
          {t('Send Export to Email')}
        </Button>,
      ]}
      title={t('Export donation data')}
    >
      <Fieldset className={styles.fieldset}>
        <Input
          label={t('Email address')}
          onBlur={handleEmailBlur}
          onChange={handleChangeEmail}
          placeholder={t('firstname.lastname@nonprofit.org')}
          type={'text'}
          validationError={emailHadFocus ? flattenError(validationErrors?.email) : undefined}
          value={email}
        />
        <div className={styles.donationFormsSelect}>
          <DonationFormsSelect isMulti={true} onChange={handleSelectForms} value={selectedForms} />
        </div>
        <DataFieldsSelect
          onSelectOptionalFields={handleSelectOptionalFields}
          selectedOptionalFields={selectedOptionalFields}
        />
        <DateRangeSelect
          customDateRange={customDateRange}
          dateRangeOptions={dateRangeOptions}
          onCustomDateRangeChange={handleCustomDateRangeChange}
          onDateRangeChange={handleDateRangeChange}
          selectedDateRange={selectedDateRange}
          validationError={validationErrors?.dateRange}
        />
      </Fieldset>
    </Modal>
  );
}

export default observer(ExportDonationsModal);
